import {createSelector, Selector} from "@reduxjs/toolkit";
import {Region, RegionEntity} from "../../../model/region";
import {OtsState} from "../../ots-store";
import {regionsAdapter} from "./reducer";
import {createParamSelector, getUseQueryResultData} from "../../../util/selector-util";

const regionSelectors = regionsAdapter.getSelectors<OtsState>(
  (state) => state.entities.regions
);

const getAllRegions = createSelector(
  [regionSelectors.selectAll],
  (regions) => {
    return regions.map((r) => RegionEntity.from(r));
  }
);

export const getCurrentRegionId: Selector<OtsState, string | undefined> = (
  state: OtsState
) => state.entities.regions.currentRegionId;

export const getCurrentRegionIdOrDefault: Selector<OtsState, string> = (
  state: OtsState
) => state.entities.regions.currentRegionId || '-1';

/**
 * Retrieve the current region from the application state.
 */
export const getCurrentRegion = createSelector(
  [getAllRegions, getCurrentRegionId],
  (regions, currentRegionId) => {
    if (currentRegionId) {
      return regions.find((r) => r.id === currentRegionId)
    }
    return undefined
  }
);

export type RegionSelectorProps = {
  regionId?: string
};

const getRegionIdParam = createParamSelector<RegionSelectorProps | undefined, string | undefined>(params => params?.regionId);

export const regionsSelector = () => createSelector(
  [getUseQueryResultData<Region[]>, getRegionIdParam],
  (data, regionId) => {
    return selectRegionsAndTransform(data ?? [], { regionId: regionId});
  }
);

export const regionSelector = () => createSelector(
  [getUseQueryResultData<Region>],
  (data) => {
    return data ? transformRegion(data) : undefined;
  }
);

export const selectRegions = (values?: Region[], selector?: RegionSelectorProps): Region[] => {
  if (!values) {
    return [];
  }

  return values.filter((v) => {
    let filterIn = true;

    if (selector?.regionId) {
      if (selector.regionId !== v.id) {
        filterIn = false;
      }
    }
    return filterIn;
  });
};

export const transformRegion = (value: Region): RegionEntity => {
  return RegionEntity.from(value)
}

export const selectRegionsAndTransform = (values?: Region[], selector?: RegionSelectorProps): RegionEntity[] => {
  const selected = selectRegions(values, selector);
  return selected.map(transformRegion);
};

